import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Section, Eyebrow, ProgressBar } from "@/components/domain/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Vote, CheckCircle2, Gift } from "lucide-react";

export default function Poll() {
  const [poll, setPoll] = useState(undefined);
  const [option, setOption] = useState(null);
  const [other, setOther] = useState("");
  const [f, setF] = useState({ name: "", email: "", whatsapp: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/polls/active").then((r) => setPoll(r.data)).catch(() => setPoll(null)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!option && !other.trim()) { toast.error("Elige un premio o escribe tu propuesta"); return; }
    setSending(true);
    try {
      const r = await api.post(`/polls/${poll.id}/vote`, { option_id: option === "other" ? null : option, other_text: other, ...f });
      setPoll(r.data); setDone(true); toast.success("¡Gracias por tu voto!");
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo registrar tu voto"); }
    finally { setSending(false); }
  };

  if (poll === undefined) return <Section className="py-20 text-center text-slate-400">Cargando…</Section>;
  if (!poll) return (
    <Section className="py-20 text-center" data-testid="poll-empty">
      <Gift className="mx-auto h-10 w-10 text-slate-300" />
      <h1 className="mt-4 text-2xl font-bold text-slate-900">No hay encuestas activas</h1>
      <p className="mt-2 text-slate-500">Pronto te preguntaremos qué premio quieres que rifemos.</p>
    </Section>
  );

  return (
    <Section className="py-12 sm:py-20">
      <div className="mx-auto max-w-2xl">
        <Eyebrow><Vote className="h-3.5 w-3.5" />Tu opinión decide</Eyebrow>
        <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="poll-question">{poll.question}</h1>
        <p className="mt-2 text-slate-500">{poll.total_votes} personas ya votaron.</p>

        {done ? (
          <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="poll-thanks">
            <div className="flex items-center gap-2 font-semibold text-emerald-800"><CheckCircle2 className="h-5 w-5" />Voto registrado</div>
            {poll.show_results ? (
              <div className="mt-5 space-y-3">
                {poll.options.map((o) => (
                  <div key={o.id} data-testid={`poll-result-${o.id}`}>
                    <div className="flex justify-between text-sm"><span className="font-medium text-slate-800">{o.text}</span><span className="font-mono text-slate-500">{o.pct}%</span></div>
                    <ProgressBar value={o.pct} className="mt-1 bg-white" />
                  </div>
                ))}
              </div>
            ) : <p className="mt-2 text-sm text-emerald-700">Publicaremos el resultado muy pronto.</p>}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="poll-form">
            <div className="grid gap-3 sm:grid-cols-2">
              {poll.options.map((o) => (
                <button type="button" key={o.id} onClick={() => setOption(o.id)} data-testid={`poll-option-${o.id}`}
                  className={`rounded-xl border p-4 text-left transition-all ${option === o.id ? "border-brand bg-brand/5 ring-2 ring-brand/30" : "border-slate-200 bg-white hover:border-brand/50"}`}>
                  <div className="flex items-center gap-3">
                    <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border ${option === o.id ? "border-brand bg-brand" : "border-slate-300"}`}>{option === o.id && <span className="h-2 w-2 rounded-full bg-white" />}</span>
                    <span className="font-semibold text-slate-900">{o.text}</span>
                  </div>
                </button>
              ))}
              {poll.allow_other && (
                <button type="button" onClick={() => setOption("other")} data-testid="poll-option-other"
                  className={`rounded-xl border p-4 text-left transition-all ${option === "other" ? "border-brand bg-brand/5 ring-2 ring-brand/30" : "border-dashed border-slate-300 bg-white hover:border-brand/50"}`}>
                  <div className="flex items-center gap-3">
                    <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border ${option === "other" ? "border-brand bg-brand" : "border-slate-300"}`}>{option === "other" && <span className="h-2 w-2 rounded-full bg-white" />}</span>
                    <span className="font-semibold text-slate-900">Otro premio…</span>
                  </div>
                </button>
              )}
            </div>
            {poll.allow_other && (
              <div>
                <Label>{option === "other" ? "¿Qué premio te gustaría? *" : "Comentarios o alguna opción que no te guste (opcional)"}</Label>
                <Textarea rows={3} value={other} onChange={(e) => setOther(e.target.value)} maxLength={300} placeholder="Escribe aquí…" className="mt-1.5" data-testid="poll-other-text" />
              </div>
            )}
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-sm font-semibold text-slate-900">Tus datos (para avisarte cuando el premio ganador se rife)</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <div><Label>Nombre *</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="poll-name" /></div>
                <div><Label>Correo *</Label><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="poll-email" /></div>
                <div><Label>WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} placeholder="33 1234 5678" data-testid="poll-whatsapp" /></div>
              </div>
            </div>
            <Button type="submit" disabled={sending} className="w-full rounded-full bg-brand py-6 text-base hover:bg-brand-hover sm:w-auto sm:px-10" data-testid="poll-submit">{sending ? "Enviando…" : "Enviar mi voto"}</Button>
          </form>
        )}
      </div>
    </Section>
  );
}
